import { cn } from '../../lib/cn'
import { formatIndex } from '../../lib/golfFormat'

export interface HandicapMovementProps {
  /**
   * `round_players.index_at_play` — the index the card was FROZEN against at
   * completion. The one number in this pair that can never be recovered later.
   */
  indexAtPlay: number | null | undefined
  /** `summary.index_after` — where the member's index stood after that day. */
  indexAfter: number | null | undefined
  /**
   * `summary.index_delta`, when the club sent one. Absent is not zero: the
   * move is DERIVED from the pair below rather than assumed, so a payload that
   * omits the delta still tells the story it has the numbers for.
   */
  indexDelta?: number | null
  className?: string
}

/**
 * What the day did to a member's Handicap Index — the round page's closing
 * statistic, and the one number on it that outlives the afternoon.
 *
 * ## It renders NOTHING rather than something reassuring
 *
 * A guest has no index and never will; a member whose round has not been
 * completed has no `index_after`; the public share shape OMITS both keys
 * entirely, deliberately (a live-derived number does not belong on a public
 * archive of one afternoon). Every one of those is a case where there is no
 * story, and a component that answered them with "—" or "0.0" would be
 * inventing one. So: both numbers or no component.
 *
 * ## Down is good, and colour never says so alone
 *
 * A falling index is a member playing better, so the fall takes the fairway
 * green and the rise takes brass-deep — but the GLYPH carries the direction
 * and the magnitude is printed beside it, and a screen reader is given the
 * word rather than the arrow. The arrow is `aria-hidden` for exactly that
 * reason: "▾" announces as anything from nothing to "black down-pointing small
 * triangle" depending on the reader.
 *
 * A move that rounds to nothing is "held steady", never "▾ 0.0". The club does
 * not dress a flat afternoon up as a direction.
 */
export function HandicapMovement({
  indexAtPlay,
  indexAfter,
  indexDelta,
  className,
}: HandicapMovementProps) {
  if (indexAtPlay === null || indexAtPlay === undefined) return null
  if (indexAfter === null || indexAfter === undefined) return null

  // Derived rather than trusted-or-defaulted: an omitted delta is a payload
  // that did not compute one, not a round that moved nobody.
  const delta = indexDelta ?? indexAfter - indexAtPlay
  // ONE decimal, because that is the resolution an index is published at — a
  // -0.04999 that prints as "0.0" beside an arrow is a move the club cannot
  // show, and `held steady` is the honest sentence for it.
  const magnitude = Math.abs(delta).toFixed(1)
  const moved = magnitude !== '0.0'
  const fell = delta < 0

  return (
    <p
      className={cn(
        'flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-sage-soft pt-2.5 text-[12px] text-ink-soft',
        className,
      )}
      data-handicap-movement={moved ? (fell ? 'down' : 'up') : 'steady'}
    >
      <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-brass-deep">Index</span>
      <b className="font-display text-[13.5px] font-medium tabular-nums text-pine">
        {formatIndex(indexAtPlay)}
      </b>
      <span aria-hidden="true" className="text-brass-deep">
        →
      </span>
      <b className="font-display text-[13.5px] font-medium tabular-nums text-pine">
        {formatIndex(indexAfter)}
      </b>
      {moved ? (
        <span className={fell ? 'font-semibold text-fairway' : 'font-semibold text-brass-deep'}>
          <span aria-hidden="true">{fell ? '▾' : '▴'}</span>
          <span className="sr-only">{fell ? 'down ' : 'up '}</span>
          <span className="tabular-nums"> {magnitude}</span>
        </span>
      ) : (
        <span>held steady</span>
      )}
    </p>
  )
}
